import type { ThemeDefinition, ThemeId } from './types';
import { ivoryBrass } from './themes/ivoryBrass';
import { graphiteAzure } from './themes/graphiteAzure';

/**
 * Every theme the product ships, in the order Settings lists them.
 *
 * Adding a third theme is a file in `themes/` and one entry here. Nothing else
 * — no component, no CSS file, no store — has to know it exists.
 */
export const THEMES: readonly ThemeDefinition[] = [ivoryBrass, graphiteAzure];

/**
 * What a user sees when they have never chosen.
 *
 * Graphite & Azure as of the phase-41 artboards: the Reports and Records
 * screens are now drawn in it, so leaving the default on ivory would ship a
 * product whose screens and its own design source disagree on every colour.
 *
 * This only moves users who never picked a theme. `applyTheme` reads the
 * persisted id first, so anyone who chose ivory keeps ivory — a release that
 * overrides a deliberate choice is a support incident, not a feature.
 */
export const DEFAULT_THEME_ID: ThemeId = graphiteAzure.id;

const BY_ID = new Map<string, ThemeDefinition>(THEMES.map((theme) => [theme.id, theme]));

/** True for an id this build actually ships. Guards persisted values. */
export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && BY_ID.has(value);
}

/**
 * Resolve an id to its definition, falling back to the default.
 *
 * Never throws: an id can arrive from localStorage written by an older or
 * newer build, and an unknown theme must degrade to the default rather than
 * blanking the app.
 */
export function getTheme(id: string | undefined | null): ThemeDefinition {
  return (id && BY_ID.get(id)) || BY_ID.get(DEFAULT_THEME_ID)!;
}
